import {
    ArrowDownTrayIcon,
    ChartBarIcon,
    ChartPieIcon,
    CheckCircleIcon,
    CurrencyDollarIcon,
    HandThumbUpIcon,
} from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import analyticsAPI from '../services/analytics';

const ExecutiveDashboard = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { addListener, isConnected } = useWebSocket();
  const [dateRange, setDateRange] = useState('30d');

  // Fetch dashboard analytics
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['dashboard-analytics', dateRange],
    queryFn: () => analyticsAPI.getDashboard(dateRange),
    refetchInterval: 30000,
  });

  // Fetch productivity metrics
  const { data: productivityData, isLoading: productivityLoading } = useQuery({
    queryKey: ['productivity', dateRange],
    queryFn: () => analyticsAPI.getProductivity(dateRange),
    refetchInterval: 60000,
  });

  // Set up WebSocket listeners for real-time updates
  useEffect(() => {
    if (!isConnected || !user?.organizationId) return;

    const handleCallUpdate = (_data) => {
      // Refresh dashboard data when call status changes
      queryClient.invalidateQueries({ queryKey: ['dashboard-analytics'] });
      queryClient.invalidateQueries({ queryKey: ['productivity'] });
    };

    const handleAgentUpdate = (_data) => {
      // Refresh dashboard data when agent status changes
      queryClient.invalidateQueries({ queryKey: ['productivity'] });
    };

    const handleOrganizationUpdate = (_data) => {
      // Refresh all dashboard data
      queryClient.invalidateQueries({ queryKey: ['dashboard-analytics'] });
      queryClient.invalidateQueries({ queryKey: ['productivity'] });
    };

    addListener('call_status_update', handleCallUpdate);
    addListener('call_completed', handleCallUpdate);
    addListener('agent_status_change', handleAgentUpdate);
    addListener('organization_update', handleOrganizationUpdate);

    return () => {
      // Cleanup listeners when component unmounts
    };
  }, [isConnected, user?.organizationId, addListener, queryClient]);

  if (dashboardLoading || productivityLoading) {
    return <LoadingSpinner />;
  }

  const data = dashboardData?.data || {};
  // eslint-disable-next-line no-unused-vars
  const productivity = productivityData?.productivity || {};

  // Use real data from backend
  const qualifiedLeads = data.qualifiedLeads || 0;
  const totalRevenue = data.totalRevenue || 0;
  const totalCost = data.totalCost || 0;
  const roi = data.roi || 0;

  // Format change values to show trend direction
  const formatChange = (value) => {
    if (value === 0 || value === undefined || value === null) return '0%';
    const formatted = Math.abs(value).toFixed(0);
    return value > 0 ? `+${formatted}%` : `-${formatted}%`;
  };

  const formatCsatChange = (value) => {
    if (value === 0 || value === undefined || value === null) return '0';
    const formatted = Math.abs(value).toFixed(1);
    return value > 0 ? `+${formatted}` : `-${formatted}`;
  };

  const metrics = [
    {
      title: 'Total Revenue Impact',
      value: `₹${(totalRevenue / 100000).toFixed(1)}L`,
      description: 'From qualified leads',
      change: formatChange(data.totalCallsChange),
      changeColor: (data.totalCallsChange || 0) >= 0 ? 'text-green-600' : 'text-red-600',
      icon: CurrencyDollarIcon,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Cost Efficiency',
      value: `₹${(data.costPerLead || 0).toFixed(2)}`,
      description: 'Per qualified lead',
      change: formatChange(data.costPerLeadChange),
      changeColor: (data.costPerLeadChange || 0) <= 0 ? 'text-green-600' : 'text-red-600', // Lower cost is better
      icon: ChartPieIcon,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Campaign ROI',
      value: `${roi}%`,
      description: 'Average return',
      change: formatChange(data.conversionRateChange),
      changeColor: (data.conversionRateChange || 0) >= 0 ? 'text-green-600' : 'text-red-600',
      icon: ChartBarIcon,
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Customer Satisfaction',
      value: `${(data.avgCSAT || 0).toFixed(1)}/5`,
      description: 'Avg CSAT score',
      change: formatCsatChange(data.csatChange),
      changeColor: (data.csatChange || 0) >= 0 ? 'text-green-600' : 'text-red-600',
      icon: HandThumbUpIcon,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
  ];

  // Calculate conversion funnel from real data
  const totalAttempts = data.totalCalls || 0;
  const connected = data.completed || 0;
  const interested = data.interested || 0;
  const qualified = qualifiedLeads; // meetings + interested
  const converted = data.meetings || 0;
  // Engaged = calls that lasted more than minimal time (interested + meetings + some portion of others)
  const engaged = interested + converted;

  const conversionFunnel = [
    {
      stage: 'Total Attempts',
      value: totalAttempts,
      percentage: 100,
      color: 'bg-blue-500'
    },
    {
      stage: 'Connected',
      value: connected,
      percentage: totalAttempts > 0 ? Math.round((connected / totalAttempts) * 100) : 0,
      color: 'bg-green-500'
    },
    {
      stage: 'Engaged',
      value: engaged,
      percentage: totalAttempts > 0 ? Math.round((engaged / totalAttempts) * 100) : 0,
      color: 'bg-purple-500'
    },
    {
      stage: 'Qualified',
      value: qualified,
      percentage: totalAttempts > 0 ? Math.round((qualified / totalAttempts) * 100) : 0,
      color: 'bg-orange-500'
    },
    {
      stage: 'Converted',
      value: converted,
      percentage: totalAttempts > 0 ? Math.round((converted / totalAttempts) * 100) : 0,
      color: 'bg-red-500'
    },
  ];

  const recentCalls = (data.recentCalls || []).slice(0, 4).map(call => ({
    name: call.name,
    emotion: call.emotion,
    date: new Date(call.timestamp).toLocaleString(),
    status: call.outcome,
    csat: call.csat,
    bgColor: call.outcome === 'scheduled' ? 'bg-green-50' : 'bg-white',
  }));

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900'>Executive Dashboard</h1>
          <p className='text-sm text-gray-600 mt-1'>Comprehensive business metrics and insights</p>
        </div>
        <div className='flex items-center space-x-3'>
          {/* Date Range Selector */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className='px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
          >
            <option value='7d'>Last 7 Days</option>
            <option value='30d'>Last 30 Days</option>
            <option value='90d'>Last 90 Days</option>
          </select>
          <button className='flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700'>
            <ArrowDownTrayIcon className='h-4 w-4 mr-2' />
            Export Report
          </button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
        {metrics.map((metric, idx) => {
          const Icon = metric.icon;
          return (
            <div key={idx} className={`${metric.bgColor} rounded-lg p-6`}>
              <div className='flex items-center justify-between mb-4'>
                <div className='p-3 rounded-lg bg-white'>
                  <Icon className={`h-6 w-6 ${metric.iconColor}`} />
                </div>
                <span className={`text-sm font-medium ${metric.changeColor}`}>{metric.change}</span>
              </div>
              <h3 className='text-lg font-semibold text-gray-900 mb-1'>{metric.title}</h3>
              <p className='text-3xl font-bold text-gray-900 mb-1'>{metric.value}</p>
              <p className='text-sm text-gray-600'>{metric.description}</p>
            </div>
          );
        })}
      </div>

      {/* Conversion Funnel */}
      <div className='bg-white rounded-lg shadow-sm p-6'>
        <h3 className='text-lg font-semibold text-gray-900 mb-4'>Conversion Funnel</h3>
        <div className='space-y-4'>
          {conversionFunnel.map((stage, idx) => (
            <div key={idx} className='flex items-center justify-between'>
              <span className='text-sm font-medium text-gray-700 w-32'>{stage.stage}</span>
              <div className='flex items-center flex-1 mx-4'>
                <div className='flex-1 bg-gray-200 rounded-full h-2'>
                  <div
                    className={`h-2 rounded-full ${stage.color}`}
                    style={{ width: `${stage.percentage}%` }}
                  ></div>
                </div>
              </div>
              <span className='text-sm text-gray-600 w-20 text-right'>
                {stage.value} ({stage.percentage}%)
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Calls */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        <div className='bg-white rounded-lg shadow-sm p-6'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4'>Recent Calls with Emotions</h3>
          <div className='space-y-3'>
            {recentCalls.map((call, idx) => (
              <div
                key={idx}
                className={`flex items-center justify-between p-4 rounded-lg ${call.bgColor}`}
              >
                <div className='flex items-center space-x-4'>
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      call.status === 'scheduled' || call.status === 'fit'
                        ? 'bg-green-100'
                        : call.status === 'connected'
                          ? 'bg-yellow-100'
                          : 'bg-gray-100'
                    }`}
                  >
                    {call.status === 'scheduled' || call.status === 'fit' ? (
                      <CheckCircleIcon className='h-5 w-5 text-green-600' />
                    ) : call.status === 'connected' ? (
                      <div className='w-2 h-2 bg-yellow-500 rounded-full'></div>
                    ) : (
                      <CheckCircleIcon className='h-5 w-5 text-gray-600' />
                    )}
                  </div>
                  <div>
                    <p className='font-medium text-gray-900'>{call.name}</p>
                    <p className='text-sm text-gray-500'>{call.date}</p>
                  </div>
                </div>
                <div className='flex items-center space-x-4'>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      call.emotion === 'interested'
                        ? 'bg-green-100 text-green-800'
                        : call.emotion === 'positive'
                          ? 'bg-gray-100 text-gray-800'
                          : call.emotion === 'neutral'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {call.emotion}
                  </span>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      call.status === 'scheduled' || call.status === 'fit'
                        ? 'bg-green-100 text-green-800'
                        : call.status === 'connected'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {call.status}
                  </span>
                  {call.csat && <p className='text-sm text-gray-600'>CSAT: {call.csat}/5</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cost Per Qualified Lead */}
      <div className='bg-green-50 rounded-lg p-6'>
        <h3 className='text-lg font-semibold text-gray-900 mb-4 flex items-center'>
          <CurrencyDollarIcon className='h-5 w-5 mr-2' />
          Cost Per Qualified Lead
        </h3>
        <div className='grid grid-cols-2 lg:grid-cols-4 gap-6'>
          <div>
            <p className='text-sm text-gray-600'>Cost / Lead</p>
            <p className='text-2xl font-bold text-green-600'>₹{(data.costPerLead || 0).toFixed(2)}</p>
          </div>
          <div>
            <p className='text-sm text-gray-600'>Total Cost</p>
            <p className='text-2xl font-bold text-blue-600'>₹{(totalCost || 0).toFixed(2)}</p>
          </div>
          <div>
            <p className='text-sm text-gray-600'>Conversion Rate</p>
            <p className='text-2xl font-bold text-purple-600'>{(data.conversionRate || 0)}%</p>
          </div>
          <div>
            <p className='text-sm text-gray-600'>Campaign ROI</p>
            <p className='text-2xl font-bold text-green-600'>{roi}%</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
